import { useState, useEffect } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Button, Typography, TextField } from '@mui/material';
import Loading from '@/component/mui/Backdrop';
import { toast } from 'react-toastify';
import { SUCCESS } from '@/lib/common/name-space';
import Header from '@/component/Header';
import { Card, CardContent, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OtpDialog from '@/component/otp/OtpDialog';
import { CreateJackpotWalletType } from '@/types';
import { createJackpotWallet } from '@/api/bet/create';
import { readJackpotWalletList } from '@/api/bet/read';

export default function AddJackpotWallet() {
  const [jackpotWallets, setJackpotWallets] = useState<CreateJackpotWalletType[]>([
    { rank: '', address: '', amount: '' },
  ]);
  const [savedJackpotWallets, setSavedJackpotWallets] = useState<CreateJackpotWalletType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const re = await readJackpotWalletList();
        if (re.status === SUCCESS && re.data && re.data.length > 0) {
          // 저장된 데이터가 있으면 저장된 데이터 목록에 표시
          setSavedJackpotWallets(re.data);
        }
        // 입력 필드는 항상 빈 상태로 유지
      } catch (error) {
        console.error('잭팟 당첨 지갑 목록 조회 실패:', error);
      }
    })();
  }, []);

  const handleAddInputs = () => {
    setJackpotWallets([
      ...jackpotWallets,
      {
        rank: '',
        address: '',
        amount: '',
      },
    ]);
  };

  const handleDelete = () => {
    if (jackpotWallets.length > 1) {
      setJackpotWallets((prevJackpotWallets) => prevJackpotWallets.slice(0, -1));
    }
  };

  const handleInputChange = (index: number, field: 'rank' | 'address' | 'amount', value: string) => {
    setJackpotWallets((prevJackpotWallets) =>
      prevJackpotWallets.map((jackpotWallet, idx) =>
        idx === index ? { ...jackpotWallet, [field]: value } : jackpotWallet,
      ),
    );
  };

  const handleOtpSuccess = async () => {
    setIsLoading(true);
    try {
      // OTP 인증 성공 시 잭팟 당첨 지갑 생성 진행
      const re = await createJackpotWallet(pendingPayload);
      if (re.status !== SUCCESS) {
        toast.error('잭팟 당첨 지갑 생성에 실패했습니다.');
        return;
      }

      toast.success('잭팟 당첨 지갑이 성공적으로 생성되었습니다.');

      // 생성 성공 후 최신 데이터 다시 조회
      const listRe = await readJackpotWalletList();
      if (listRe.status === SUCCESS && listRe.data && listRe.data.length > 0) {
        // 저장된 데이터 목록 업데이트
        setSavedJackpotWallets(listRe.data);
      }

      // 입력 필드는 초기 상태로 리셋
      setJackpotWallets([{ rank: '', address: '', amount: '' }]);
      setIsOtpDialogOpen(false);
      setPendingPayload(null);
    } catch (e) {
      console.log(e);
      toast.error('잭팟 당첨 지갑 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpClose = () => {
    setIsOtpDialogOpen(false);
    setPendingPayload(null);
  };

  const handleCreate = async () => {
    // 필수 입력값 검증
    const validJackpotWallets = jackpotWallets.filter(
      (item) => item.rank.trim() !== '' && item.address.trim() !== '' && item.amount.trim() !== '',
    );

    if (validJackpotWallets.length === 0) {
      toast.error('추가할 잭팟 당첨 지갑 데이터가 없습니다.');
      return;
    }

    // 주소 형식 검증 (간단한 이더리움 주소 형식)
    const addressRegex = /^0x[a-fA-F0-9]{40}$/;
    for (const jackpotWallet of validJackpotWallets) {
      if (!addressRegex.test(jackpotWallet.address)) {
        toast.error('올바른 지갑 주소 형식이 아닙니다.');
        return;
      }
    }

    // rank 중복 검증
    const ranks = validJackpotWallets.map((item) => item.rank);
    const uniqueRanks = new Set(ranks);
    if (ranks.length !== uniqueRanks.size) {
      toast.error('랭킹은 중복될 수 없습니다.');
      return;
    }

    // payload 생성 및 OTP 인증 다이얼로그 열기
    const payload = validJackpotWallets.map((item) => ({
      rank: item.rank,
      address: item.address,
      amount: item.amount,
    }));

    setPendingPayload(payload);
    setIsOtpDialogOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flex: 1,
          flexDirection: 'column',
          gap: { xs: '16px', sm: '0px' },
          height: '100vh',
          maxHeight: '100vh',
        }}
      >
        <Card>
          <Header title="관리자 지정 잭팟 당첨 지갑 설정" />
          <Divider />
          <Box mb={2} />
          <CardContent sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Box p={2}>
              <Grid container spacing={3} mb={2}>
                <Grid xs={4} sm={4}>
                  <Typography variant="h6">랭킹</Typography>
                </Grid>
                <Grid xs={4} sm={4}>
                  <Typography variant="h6">지갑 주소</Typography>
                </Grid>
                <Grid xs={4} sm={4}>
                  <Typography variant="h6">수량(비율이 아닌 받을 포인트)</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box p={2}>
              {jackpotWallets.map((jackpotWallet: CreateJackpotWalletType, index: number) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid xs={4} sm={4}>
                    <TextField
                      fullWidth
                      label="랭킹"
                      value={jackpotWallet.rank}
                      onChange={(e) => handleInputChange(index, 'rank', e.target.value)}
                      placeholder="1"
                      size="small"
                      type="number"
                    />
                  </Grid>
                  <Grid xs={4} sm={4}>
                    <TextField
                      fullWidth
                      label="지갑 주소"
                      value={jackpotWallet.address}
                      onChange={(e) => handleInputChange(index, 'address', e.target.value)}
                      placeholder="0x..."
                      size="small"
                    />
                  </Grid>
                  <Grid xs={4} sm={4}>
                    <TextField
                      fullWidth
                      label="수량"
                      value={jackpotWallet.amount}
                      onChange={(e) => handleInputChange(index, 'amount', e.target.value)}
                      placeholder="0"
                      size="small"
                      type="number"
                    />
                  </Grid>
                </Grid>
              ))}
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <Button fullWidth variant="outlined" color="secondary" onClick={handleAddInputs}>
                    추가
                  </Button>
                </Grid>
                <Grid xs={6}>
                  <Button fullWidth variant="outlined" color="secondary" onClick={handleDelete}>
                    삭제
                  </Button>
                </Grid>
                <Grid xs={12}>
                  <Button fullWidth variant="contained" color="primary" onClick={handleCreate}>
                    잭팟 당첨 지갑 설정
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* 저장된 데이터 표시 */}
            {savedJackpotWallets.length > 0 && (
              <Box p={2} sx={{ mt: 3, borderTop: '1px solid #e0e0e0', pt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  저장된 잭팟 당첨 지갑
                </Typography>
                <Grid container spacing={3} mb={2}>
                  <Grid xs={4} sm={4}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      랭킹
                    </Typography>
                  </Grid>
                  <Grid xs={4} sm={4}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      지갑 주소
                    </Typography>
                  </Grid>
                  <Grid xs={4} sm={4}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      수량
                    </Typography>
                  </Grid>
                </Grid>
                {savedJackpotWallets.map((savedWallet: CreateJackpotWalletType, index: number) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid xs={4} sm={4}>
                      <Typography variant="body2">{savedWallet.rank}</Typography>
                    </Grid>
                    <Grid xs={4} sm={4}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {savedWallet.address}
                      </Typography>
                    </Grid>
                    <Grid xs={4} sm={4}>
                      <Typography variant="body2">{savedWallet.amount}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
      <Loading isLoading={isLoading} />

      {/* OTP 인증 다이얼로그 */}
      <OtpDialog
        open={isOtpDialogOpen}
        onClose={handleOtpClose}
        onSuccess={handleOtpSuccess}
        title="잭팟 당첨 지갑 설정 인증"
        description="잭팟 당첨 지갑 설정을 위해 OTP 인증이 필요합니다."
        isLoading={isLoading}
      />
    </>
  );
}
